import React, { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "../../lib/api";
import { Window, Dialog, ConfirmDialog, EmptyState, Loading } from "../../components/VB";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, DollarSign, Globe, Settings as SettingsIcon, Wallet, CheckCircle2, FileSpreadsheet, Twitter } from "lucide-react";

const usd = (n) => "$ " + (parseFloat(n || 0)).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const idr = (n) => "Rp " + Math.round(parseFloat(n || 0)).toLocaleString("id-ID");

// Harga tetap order "Material Only" — SAMA PERSIS dengan konstanta di backend (server.py),
// dipakai di sini murni untuk preview total sebelum submit.
const MATERIAL_ONLY_PRICE_USD = 11;
const MATERIAL_ONLY_DOMAIN_LABEL = "MATERIAL ONLY";
// Sentinel Type Web untuk Material Only — order ini bukan pembuatan website, jadi tidak ada
// pilihan Standart/Proper sama sekali. Nilai ini yang dipakai sebagai kunci lookup Rate Harga
// Listing (harus SAMA PERSIS dengan MATERIAL_ONLY_TYPE_WEB_LABEL di backend) — admin mengatur
// rate-nya lewat Pengaturan → Rate Harga Listing dengan memilih opsi "Material Only".
const MATERIAL_ONLY_TYPE_WEB = "Material Only";

// Order "Rebrand" — alur SAMA PERSIS dengan order Website (semua field aktif/wajib), TAPI harga
// web yang tampil/ditagihkan hanya REBRAND_PRICE_MULTIPLIER dari harga Type Web (harus SAMA
// PERSIS dengan konstanta di backend). Komponen lain (domain, Rate Listing, tambahan) tidak
// dipotong.
const REBRAND_PRICE_MULTIPLIER = 0.5;

// Terima link Twitter/X lengkap (https://x.com/nama, https://twitter.com/nama) ATAU cuma
// handle mentah ("@nama"/"nama") — selalu hasilkan URL yang benar-benar bisa diklik.
const twitterHref = (v) => {
  const s = (v || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return `https://x.com/${s.replace(/^@/, "")}`;
};

// Type Web "Proper" ditandai warna Merah (bukan biru seperti Standart/lainnya) — permintaan
// eksplisit user, bukan cuma soal estetika: biar langsung kelihatan beda tanpa baca teksnya.
const isProperType = (t) => (t || "").trim().toLowerCase() === "proper";

// "Project From" — Select2-style searchable combobox untuk order Listing Only, menandai project
// ASLI (Website/Rebrand/dst.) mana yang domain & web-nya sudah lunas periode sebelumnya, dan
// sekarang cuma ditagih Rate Listing-nya di periode ini. Dibangun sebagai <input> + dropdown
// manual (bukan komponen shadcn Command+Popover yang memang ada di project ini tapi TIDAK pernah
// dipakai di halaman admin manapun) supaya gaya visualnya konsisten dengan komponen vb-* lain.
// Reuse endpoint /projects?search= yang sudah ada — bukan endpoint baru. Hasil pencarian
// difilter di client supaya project Listing Only lain tidak bisa dipilih sebagai sumbernya
// sendiri (mencegah rantai Listing Only -> Listing Only).
function ProjectFromPicker({ value, initialLabel, onPick }) {
  const [query, setQuery] = useState(initialLabel || "");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => { setQuery(initialLabel || ""); }, [initialLabel]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const runSearch = (q) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/projects", { params: { search: q, all_periode: true } });
        setResults((data || []).filter((p) => p.type_order !== "LISTING_ONLY" && p.id !== value));
      } catch (e) {
        setResults([]);
      }
      setLoading(false);
    }, 300);
  };

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <input
        className="vb-input"
        placeholder="Cari Nama Project atau Domain..."
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); runSearch(e.target.value); }}
        onFocus={() => { setOpen(true); runSearch(query); }}
        data-testid="pj-source-project-search"
      />
      {open && (
        <div style={{ position: "absolute", zIndex: 20, top: "100%", left: 0, right: 0, background: "white", border: "1px solid #ccc", maxHeight: 220, overflow: "auto", boxShadow: "0 4px 10px rgba(0,0,0,0.15)" }}>
          {loading ? (
            <div style={{ padding: 8, fontSize: 11, color: "#666" }}>Mencari...</div>
          ) : results.length === 0 ? (
            <div style={{ padding: 8, fontSize: 11, color: "#666" }}>Tidak ada project cocok. Ketik nama project atau domain untuk mencari.</div>
          ) : (
            results.map((p) => (
              <div
                key={p.id}
                style={{ padding: "6px 8px", fontSize: 12, cursor: "pointer", borderBottom: "1px solid #f0f0f0" }}
                onMouseDown={(e) => { e.preventDefault(); onPick(p); setQuery(p.nama_project); setOpen(false); }}
                data-testid={`pj-source-project-option-${p.id}`}
              >
                <div style={{ fontWeight: 600 }}>{p.nama_project}</div>
                <div style={{ fontSize: 10, color: "#666", fontFamily: "Consolas, monospace" }}>{p.domain} · {p.type_web}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Renders USD value on top with IDR conversion small below (realtime rate via /api/exchange-rate)
const UsdIdr = ({ n, rate, align = "right", color, fontSize, fontWeight, invert = false, prefix = "" }) => {
  const val = parseFloat(n || 0);
  const rp = val * (parseFloat(rate) || 16000);
  return (
    <span style={{ display: "inline-block", textAlign: align, lineHeight: 1.15 }}>
      <span style={{ color, fontSize, fontWeight }}>{prefix}{usd(val)}</span>
      <span style={{ display: "block", fontSize: 9, color: invert ? "rgba(255,255,255,0.75)" : "#888", fontWeight: 400, fontStyle: "italic" }}>
        ≈ {idr(rp)}
      </span>
    </span>
  );
};

// Helper function to get current payment periode based on today's date
const getCurrentPeriode = () => {
  const today = new Date().getDate();
  // If date is 1-15: show period 1-15 (periode 15)
  // If date is 16-30/31: show period 16-30/31 (periode 30)
  return today <= 15 ? "15" : "30";
};

export default function ProjectPayment() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);  // untuk "Tautkan ke Pengguna" pada Client / Group Client
  const [exts, setExts] = useState([]);
  const [types, setTypes] = useState([]);
  const [devs, setDevs] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [listings, setListings] = useState([]);
  const [listingRates, setListingRates] = useState([]);
  const [tambahanList, setTambahanList] = useState([]);
  const [reks, setReks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().slice(0, 10);
  const [filterBulan, setFilterBulan] = useState(today.slice(0, 7));
  const [filterPeriode, setFilterPeriode] = useState(getCurrentPeriode);
  const [filterClient, setFilterClient] = useState("");
  const [filterListing, setFilterListing] = useState("");
  const [filterListingId, setFilterListingId] = useState("");
  const [filterGroup, setFilterGroup] = useState("");
  const [search, setSearch] = useState("");
  const [allPeriode, setAllPeriode] = useState(false);

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const empty = { nama_project: "", type_order: "WEBSITE", domain: "", link_twitter: "", type_web: "", client_id: "", developer_id: "", designer_id: "", listing_id: "", source_project_id: "", tanggal_pembuatan: today, tambahan: 0, tambahan_keterangan: "", free_domain: false, tambahan_ids: [], catatan: "" };
  const [form, setForm] = useState(empty);
  const [del, setDel] = useState(null);

  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ payment_date: today, amount_paid: 0, metode: "Transfer", catatan: "", mode: null });

  // Review permintaan revisi (Group Client) — dialog abu-abu "Pending Rev".
  const [revisionView, setRevisionView] = useState(null);
  const approveRevision = async () => {
    try {
      await api.patch(`/projects/${revisionView.id}/revisi/approve`);
      toast.success("Revisi disetujui — status kembali normal");
      setRevisionView(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Settings management
  const [tab, setTab] = useState("projects"); // projects, recap, settings
  const [recap, setRecap] = useState([]);
  const [recapGroupPeriode, setRecapGroupPeriode] = useState("");
  const [recapGroups, setRecapGroups] = useState([]);

  const loadRecap = async () => {
    try {
      const { data } = await api.get("/projects/recap-periode");
      setRecap(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadRecapGroup = async (periode) => {
    setRecapGroupPeriode(periode);
    try {
      const { data } = await api.get("/projects/recap-group", { params: periode ? { periode } : {} });
      setRecapGroups(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { if (tab === "recap") { loadRecap(); loadRecapGroup(""); } }, [tab]);

  const load = async () => {
    setLoading(true);
    try {
      const listingParam = filterListing || undefined;
      const listingIdParam = filterListingId || undefined;
      const clientParam = filterClient || undefined;
      const groupParam = filterGroup || undefined;
      const searchParam = (search || "").trim() || undefined;
      const params = {
        bulan: filterBulan,
        periode: filterPeriode,
        client_id: clientParam,
        listing_status: listingParam,
        listing_id: listingIdParam,
        group_id: groupParam,
        search: searchParam,
        all_periode: allPeriode ? true : undefined,
      };
      const [p, c, g, e, t, d, ds, ls, lr, s, tb, rk, us] = await Promise.all([
        api.get("/projects", { params }),
        api.get("/clients"), api.get("/project-groups"),
        api.get("/domain-ext"), api.get("/web-types"), api.get("/developers"),
        api.get("/designers"), api.get("/listings"), api.get("/listing-rates"),
        api.get("/projects/summary", { params }),
        api.get("/project-tambahan"),
        api.get("/rekening"),
        api.get("/users"),
      ]);
      setItems(p.data); setClients(c.data); setGroups(g.data); setExts(e.data); setTypes(t.data); setDevs(d.data); setDesigners(ds.data); setSummary(s.data);
      setListings(ls.data); setListingRates(lr.data);
      setTambahanList(tb.data); setReks(rk.data); setUsers(us.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterBulan, filterPeriode, filterClient, filterListing, filterListingId, filterGroup, search, allPeriode]);

  const isMaterialOnly = form.type_order === "MATERIAL_ONLY";
  const isRebrand = form.type_order === "REBRAND";
  // Listing Only — bukan order website/domain baru, murni entry untuk menagih Rate Listing
  // periode ini atas website yang domain+web-nya sudah lunas di periode sebelumnya (lihat
  // "Project From" di bawah). Domain/Type Web disabled & auto-terisi dari project sumber.
  const isListingOnly = form.type_order === "LISTING_ONLY";

  // Auto-calculate preview prices
  const previewExt = (() => {
    const d = (form.domain || "").toLowerCase().trim();
    for (const dbl of [".co.id", ".or.id", ".web.id", ".my.id", ".ac.id", ".sch.id", ".go.id"]) {
      if (d.endsWith(dbl)) return dbl;
    }
    return d.includes(".") ? "." + d.split(".").pop() : "";
  })();
  // Material Only: tidak ada domain sama sekali (price_domain selalu 0) dan harga web dipaksa
  // flat MATERIAL_ONLY_PRICE_USD, bukan dari tabel Type Web — sama persis logika backend.
  // Listing Only: domain+web SUDAH lunas di periode sebelumnya — keduanya $0 lagi di sini.
  const previewPriceDomain = (isMaterialOnly || isListingOnly) ? 0 : (form.free_domain ? 0 : (exts.find((x) => x.ext === previewExt)?.price_usd || 0));
  // Harga Type Web mentah (sebelum potongan Rebrand) — dipakai juga untuk hint di form.
  const rawPriceWeb = types.find((x) => x.nama === form.type_web)?.price_usd || 0;
  // Rebrand: harga web yang ditagihkan hanya 50% dari harga Type Web (komponen lain tetap penuh).
  const previewPriceWeb = isMaterialOnly
    ? MATERIAL_ONLY_PRICE_USD
    : isListingOnly
    ? 0
    : (isRebrand ? rawPriceWeb * REBRAND_PRICE_MULTIPLIER : rawPriceWeb);
  // Rate Listing HANYA dihitung kalau Petugas Listing benar-benar dipilih — kosong = tidak ada
  // tambahan biaya listing sama sekali (sama persis logika di backend pj_create/pj_update).
  // Untuk Material Only, kuncinya sentinel MATERIAL_ONLY_TYPE_WEB (bukan form.type_web, yang
  // sudah di-disable/tidak dipakai di mode ini). Untuk Listing Only, form.type_web sudah otomatis
  // terisi Type Web ASLI dari "Project From" (lihat handleSourceProjectPick), jadi lookup-nya
  // sama persis seperti order Website biasa — bukan sentinel.
  const listingRateLookupKey = isMaterialOnly ? MATERIAL_ONLY_TYPE_WEB : form.type_web;
  const previewPriceListing = form.listing_id ? (listingRates.find((r) => r.type_web === listingRateLookupKey)?.rate_usd || 0) : 0;

  // Compute selected tambahan: which goes to project total vs which is paid from rekening
  const [usdIdrRate, setUsdIdrRate] = useState(16000);
  useEffect(() => {
    api.get("/exchange-rate").then(({ data }) => setUsdIdrRate(parseFloat(data?.rates?.IDR || 16000))).catch(() => {});
  }, []);
  const toUSD = (n, cur) => (cur === "USD" ? parseFloat(n || 0) : parseFloat(n || 0) / usdIdrRate);
  const selectedTambahan = tambahanList.filter((t) => (form.tambahan_ids || []).includes(t.id));
  const tambahanToTotalUSD = selectedTambahan.filter((t) => !t.rekening_id_debet).reduce((a, t) => a + toUSD(t.nominal, t.currency), 0);
  const tambahanFromRekening = selectedTambahan.filter((t) => !!t.rekening_id_debet);
  const previewTotal = (parseFloat(previewPriceDomain) + parseFloat(previewPriceWeb) + parseFloat(previewPriceListing) + parseFloat(form.tambahan || 0) + tambahanToTotalUSD).toFixed(2);

  const openCreate = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (it) => { setForm({ ...empty, ...it }); setEditId(it.id); setOpen(true); };

  // Ganti Type Order — pindah ke Material Only otomatis mengunci domain ke "MATERIAL ONLY",
  // Type Web ke sentinel "Material Only" (order ini bukan pembuatan website, tidak ada pilihan
  // Standart/Proper), dan mengosongkan Developer/Designer (uang tidak boleh masuk ke keduanya
  // untuk order jenis ini, ditegakkan ulang di backend juga — ini murni supaya form langsung
  // konsisten begitu dipilih). Balik ke Website mengosongkan lagi domain & type_web-nya supaya
  // admin mengisi dari nol, bukan diam-diam membawa nilai sentinel Material Only.
  const handleTypeOrderChange = (value) => {
    if (value === "MATERIAL_ONLY") {
      setForm({ ...form, type_order: value, domain: MATERIAL_ONLY_DOMAIN_LABEL, type_web: MATERIAL_ONLY_TYPE_WEB, free_domain: false, developer_id: "", designer_id: "", source_project_id: "" });
    } else if (value === "LISTING_ONLY") {
      // Domain & Type Web dikosongkan dulu — baru terisi otomatis begitu admin memilih
      // "Project From" (lihat handleSourceProjectPick). Developer/Designer dikosongkan karena
      // tidak ada pekerjaan dev/design baru periode ini (uang tidak masuk ke keduanya, ditegakkan
      // ulang di backend juga).
      setForm({ ...form, type_order: value, domain: "", type_web: "", free_domain: false, developer_id: "", designer_id: "" });
    } else {
      setForm({
        ...form,
        type_order: value,
        domain: form.domain === MATERIAL_ONLY_DOMAIN_LABEL ? "" : form.domain,
        type_web: form.type_web === MATERIAL_ONLY_TYPE_WEB ? "" : form.type_web,
        source_project_id: "",
      });
    }
  };

  // Dipanggil ProjectFromPicker saat admin memilih/melepas "Project From" — auto-isi Domain &
  // Type Web dari project sumber (disabled di UI, tapi state tetap harus benar supaya ikut
  // terkirim ke backend saat Simpan). melepas pilihan (proj=null) mengosongkan lagi keduanya.
  const handleSourceProjectPick = (proj) => {
    setForm({
      ...form,
      source_project_id: proj ? proj.id : "",
      domain: proj ? proj.domain : "",
      type_web: proj ? proj.type_web : "",
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, tambahan: parseFloat(form.tambahan || 0), tambahan_ids: form.tambahan_ids || [] };
      if (editId) await api.put(`/projects/${editId}`, payload);
      else await api.post("/projects", payload);
      toast.success("Project tersimpan"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/projects/${del.id}`); toast.success("Dihapus"); setDel(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const submitPay = async () => {
    try {
      await api.post(`/projects/${payTarget.id}/payment`, { ...payForm, amount_paid: parseFloat(payForm.amount_paid) });
      toast.success("Pembayaran tercatat"); setPayTarget(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Window
      title="Payment Website (USD)"
      actions={
        <>
          <button className={`vb-btn ${tab === "projects" ? "vb-btn-primary" : ""}`} onClick={() => setTab("projects")} data-testid="tab-projects">Projects</button>
          <button className={`vb-btn ${tab === "recap" ? "vb-btn-primary" : ""}`} onClick={() => setTab("recap")} data-testid="tab-recap">Rekap Periode</button>
          <button className={`vb-btn ${tab === "settings" ? "vb-btn-primary" : ""}`} onClick={() => setTab("settings")} data-testid="tab-settings"><SettingsIcon className="w-3 h-3 inline mr-1" />Pengaturan</button>
        </>
      }
    >
      {tab === "projects" && (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
            <select
              className="vb-select"
              style={{ width: 140, opacity: allPeriode ? 0.5 : 1 }}
              value={filterPeriode}
              onChange={(e) => setFilterPeriode(e.target.value)}
              disabled={allPeriode}
              data-testid="filter-periode"
            >
              <option value="15">Periode 1-15</option>
              <option value="30">Periode 16-30/31</option>
              <option value="all">Semua Periode</option>
            </select>
            <input
              type="month"
              className="vb-input"
              style={{ width: 160, opacity: allPeriode ? 0.5 : 1 }}
              value={filterBulan}
              onChange={(e) => setFilterBulan(e.target.value)}
              disabled={allPeriode}
              data-testid="filter-bulan"
            />
            <select
              className="vb-select"
              style={{ width: 160 }}
              value={filterGroup}
              onChange={(e) => setFilterGroup(e.target.value)}
              data-testid="filter-group"
              title="Filter by Group Client"
            >
              <option value="">Semua Group</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.nama}</option>)}
            </select>
            <select
              className="vb-select"
              style={{ width: 180 }}
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              data-testid="filter-client"
              title="Filter by Take By (client)"
            >
              <option value="">Semua Take By</option>
              {clients
                .filter((c) => !filterGroup || c.group_id === filterGroup)
                .map((c) => <option key={c.id} value={c.id}>{c.nama}{c.group_nama ? ` (${c.group_nama})` : ""}</option>)}
            </select>
            <select
              className="vb-select"
              style={{ width: 150 }}
              value={filterListing}
              onChange={(e) => setFilterListing(e.target.value)}
              data-testid="filter-listing"
              title="Filter by status listing"
            >
              <option value="">Semua Listing</option>
              <option value="Belum Listing">Belum Listing</option>
              <option value="Sudah Listing">Sudah Listing</option>
            </select>
            <select
              className="vb-select"
              style={{ width: 170 }}
              value={filterListingId}
              onChange={(e) => setFilterListingId(e.target.value)}
              data-testid="filter-listing-id"
              title="Filter by petugas Listing yang bertugas"
            >
              <option value="">Semua Petugas Listing</option>
              {listings.map((l) => <option key={l.id} value={l.id}>{l.nama}</option>)}
            </select>
            <input
              type="text"
              className="vb-input"
              style={{ width: 220 }}
              placeholder="🔍 Cari nama project / domain..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="filter-search"
            />
            <label
              style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, padding: "3px 6px", background: allPeriode ? "#e8f4ff" : "#f5f5f5", border: "1px solid " + (allPeriode ? "#003399" : "#ccc"), cursor: "pointer", fontWeight: 600 }}
              title="Jika dicentang, pencarian tidak dibatasi filter bulan/periode (cari di semua periode)"
            >
              <input
                type="checkbox"
                checked={allPeriode}
                onChange={(e) => setAllPeriode(e.target.checked)}
                data-testid="filter-all-periode"
              />
              Semua Periode
            </label>
            {(filterClient || filterListing || filterListingId || filterGroup || search) && (
              <button
                className="vb-btn"
                onClick={() => { setFilterClient(""); setFilterListing(""); setFilterListingId(""); setFilterGroup(""); setSearch(""); }}
                data-testid="filter-reset"
                title="Reset filter"
              >
                ✕ Reset
              </button>
            )}
            <button className="vb-btn-primary" onClick={openCreate} data-testid="add-project"><Plus className="w-3 h-3 inline mr-1" />Tambah Project</button>
            <ExportExcelButton filterBulan={filterBulan} filterPeriode={filterPeriode} />
            <span
              style={{ marginLeft: "auto", fontSize: 11, color: "#003399", background: "#eef4ff", border: "1px solid #b0c8f0", padding: "3px 8px", borderRadius: 3, fontFamily: "Consolas, monospace" }}
              data-testid="usd-idr-rate-indicator"
              title="Kurs realtime dari api.exchangerate.fun, cache 6 jam"
            >
              💱 1 USD ≈ {idr(usdIdrRate)}
            </span>
          </div>

          {summary && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8, marginBottom: 12 }}>
              <div className="vb-statcard"><div className="vb-statcard-label">Total Project</div><div className="vb-statcard-value">{summary.count}</div></div>
              <div className="vb-statcard">
                <div className="vb-statcard-label">Revenue {summary.total_revenue_gross_usd != null && Math.abs(summary.total_revenue_gross_usd - summary.total_revenue_usd) > 0.01 ? "(net rating)" : ""}</div>
                <div className="vb-statcard-value" style={{ fontSize: 18 }}><UsdIdr n={summary.total_revenue_usd} rate={usdIdrRate} align="left" color="#003399" fontSize={18} fontWeight={700} /></div>
                {summary.total_revenue_gross_usd != null && Math.abs(summary.total_revenue_gross_usd - summary.total_revenue_usd) > 0.01 && (
                  <div style={{ fontSize: 10, color: "#999" }}>bruto: {usd(summary.total_revenue_gross_usd)}</div>
                )}
              </div>
              <div className="vb-statcard"><div className="vb-statcard-label">Terbayar</div><div className="vb-statcard-value" style={{ fontSize: 18 }}><UsdIdr n={summary.total_paid_usd} rate={usdIdrRate} align="left" color="#4f8a10" fontSize={18} fontWeight={700} /></div></div>
              <div className="vb-statcard"><div className="vb-statcard-label">Outstanding</div><div className="vb-statcard-value" style={{ fontSize: 18 }}><UsdIdr n={summary.total_outstanding_usd} rate={usdIdrRate} align="left" color="#d8000c" fontSize={18} fontWeight={700} /></div></div>
              <div className="vb-statcard"><div className="vb-statcard-label">Lunas</div><div className="vb-statcard-value" style={{ color: "#4f8a10" }}>{summary.count_lunas}</div></div>
              <div className="vb-statcard"><div className="vb-statcard-label">Pending</div><div className="vb-statcard-value" style={{ color: "#d8000c" }}>{summary.count_pending}</div></div>
              <div className="vb-statcard" style={{ background: (summary.count_revision_pending || 0) > 0 ? "#f3f4f6" : undefined }}>
                <div className="vb-statcard-label">Pending Rev</div>
                <div className="vb-statcard-value" style={{ color: (summary.count_revision_pending || 0) > 0 ? "#4b5563" : "#999" }}>{summary.count_revision_pending || 0}</div>
              </div>
            </div>
          )}

          {loading ? <Loading /> : items.length === 0 ? <EmptyState message="Belum ada project" /> : (
            <div style={{ overflowX: "auto" }}>
              <table className="vb-table">
                <thead>
                  <tr>
                    <th>Nama Project</th><th>Domain</th>
                    <th style={{ textAlign: "right" }}>Price Domain</th>
                    <th>Type</th><th style={{ textAlign: "right" }}>Price Web</th>
                    <th style={{ textAlign: "right" }}>Rate Listing</th>
                    <th>Take By (Client/Group)</th>
                    <th style={{ textAlign: "right" }}>Tambahan</th>
                    <th style={{ textAlign: "right" }}>Total</th>
                    <th style={{ textAlign: "center" }}>Rating</th>
                    <th>Tgl Buat</th><th>Due</th>
                    <th>Developer</th><th>Petugas Listing</th><th>Listing</th><th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const isListing = it.listing_status === "Sudah Listing";
                    const isMaterialOnlyRow = it.type_order === "MATERIAL_ONLY";
                    const isRebrandRow = it.type_order === "REBRAND";
                    const isListingOnlyRow = it.type_order === "LISTING_ONLY";
                    const rowBg = isListing ? "#d6f3c1" : "#fff7b3";  // hijau mentah / kuning mentah
                    return (
                    <tr key={it.id} style={{ background: rowBg }} data-testid={`pj-row-${it.id}`}>
                      <td>
                        <strong>{it.nama_project}</strong>
                        {isMaterialOnlyRow && (
                          <span className="vb-badge" style={{ marginLeft: 6, background: "#6b7280", color: "white", fontSize: 9, fontWeight: 700 }}>MATERIAL ONLY</span>
                        )}
                        {isRebrandRow && (
                          <span className="vb-badge" style={{ marginLeft: 6, background: "#7c3aed", color: "white", fontSize: 9, fontWeight: 700 }}>REBRAND</span>
                        )}
                        {isListingOnlyRow && (
                          <span className="vb-badge" style={{ marginLeft: 6, background: "#0891b2", color: "white", fontSize: 9, fontWeight: 700 }}>LISTING ONLY</span>
                        )}
                        {isListingOnlyRow && it.source_project_nama && (
                          <div style={{ fontSize: 10, color: "#0891b2", marginTop: 2 }}>
                            dari: {it.source_project_nama}
                          </div>
                        )}
                      </td>
                      <td>
                        {isMaterialOnlyRow ? (
                          <span style={{ fontFamily: "Consolas, monospace", fontSize: 11, color: "#666", fontWeight: 700 }} data-testid={`pj-domain-link-${it.id}`}>
                            MATERIAL ONLY
                          </span>
                        ) : (
                          <a
                            href={`https://${(it.domain || "").replace(/^https?:\/\//, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#003399", textDecoration: "underline", cursor: "pointer", fontFamily: "Consolas, monospace", fontSize: 11 }}
                            data-testid={`pj-domain-link-${it.id}`}
                            title="Buka website di tab baru"
                          >
                            {it.domain}
                          </a>
                        )}
                        {it.link_twitter && (
                          <a
                            href={twitterHref(it.link_twitter)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ marginLeft: 6, color: "#1DA1F2", verticalAlign: "middle" }}
                            title={`Twitter: ${it.link_twitter}`}
                            data-testid={`pj-twitter-link-${it.id}`}
                          >
                            <Twitter className="w-3 h-3 inline" />
                          </a>
                        )}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {it.free_domain ? (
                          <span><span style={{ textDecoration: "line-through", color: "#999" }}>{usd(0)}</span> <span className="vb-badge vb-badge-approved" style={{ fontSize: 9 }}>FREE</span></span>
                        ) : <UsdIdr n={it.price_domain} rate={usdIdrRate} />}
                      </td>
                      <td><span className={`vb-badge ${isProperType(it.type_web) ? "vb-badge-rejected" : "vb-badge-izin"}`}>{it.type_web}</span></td>
                      <td style={{ textAlign: "right" }}><UsdIdr n={it.price_web} rate={usdIdrRate} /></td>
                      <td style={{ textAlign: "right" }}><UsdIdr n={it.price_listing} rate={usdIdrRate} /></td>
                      <td>{it.client_nama} <span style={{ fontSize: 10, color: "#666" }}>({it.group_nama})</span></td>
                      <td style={{ textAlign: "right" }}>
                        <UsdIdr n={parseFloat(it.tambahan || 0) + parseFloat(it.tambahan_total_to_project || 0)} rate={usdIdrRate} />
                        {(it.tambahan_breakdown || []).length > 0 && (
                          <div style={{ fontSize: 10, color: "#666", fontStyle: "italic" }}>
                            {(it.tambahan_breakdown || []).map((b) => b.nama).join(", ")}
                          </div>
                        )}
                        {it.tambahan_keterangan && (
                          <div style={{ fontSize: 10, color: "#666", fontStyle: "italic" }}>{it.tambahan_keterangan}</div>
                        )}
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 700 }}>
                        <UsdIdr n={it.total_payment} rate={usdIdrRate} color="#003399" fontWeight={700} />
                        {((it.pay_percent ?? 100) < 100 || parseFloat(it.tip_amount || 0) > 0) && (
                          <div style={{ fontSize: 10, color: "#166534", fontWeight: 700 }}>bayar: {usd(it.amount_due)}</div>
                        )}
                        {parseFloat(it.tip_amount || 0) > 0 && (
                          <div style={{ fontSize: 9, color: "#7c3aed" }}>+tip {usd(it.tip_amount)}</div>
                        )}
                      </td>
                      <td style={{ textAlign: "center", fontSize: 11, whiteSpace: "nowrap" }}>
                        {(() => { const rEff = it.rating_effective ?? 5; return (
                          <>
                            <span style={{ color: "#f5a623", letterSpacing: -1 }}>{"★".repeat(rEff)}</span>
                            <span style={{ color: "#ddd", letterSpacing: -1 }}>{"★".repeat(5 - rEff)}</span>
                            {(it.pay_percent ?? 100) < 100
                              ? <div style={{ color: "#7c3aed", fontWeight: 700 }}>{it.pay_percent}%</div>
                              : !it.client_rating && <div style={{ color: "#bbb", fontSize: 9 }}>default</div>}
                          </>
                        ); })()}
                      </td>
                      <td style={{ fontSize: 11 }}>{it.tanggal_pembuatan}</td>
                      <td style={{ fontSize: 11, color: "#d8000c" }}>{it.due_date}</td>
                      <td>{it.developer_nama}</td>
                      <td>{it.listing_nama}</td>
                      <td>
                        {it.revision_pending ? (
                          <button
                            className="vb-badge"
                            style={{ background: "#9ca3af", color: "white", border: "1px solid #6b7280", fontSize: 10, fontWeight: 700, cursor: "pointer" }}
                            onClick={() => setRevisionView(it)}
                            title="Ada permintaan revisi dari Group Client — klik untuk lihat & setujui"
                            data-testid={`pj-listing-${it.id}`}
                          >
                            🔄 Pending Rev
                          </button>
                        ) : isListing ? (
                          <span className="vb-badge" style={{ background: "#7bc043", color: "white", border: "1px solid #4f8a10", fontSize: 10, fontWeight: 700 }} data-testid={`pj-listing-${it.id}`}>
                            <CheckCircle2 className="w-3 h-3 inline mr-1" />Sudah Listing
                          </span>
                        ) : (
                          <span className="vb-badge" style={{ background: "#e8c000", color: "#5f4300", border: "1px solid #b89a00", fontSize: 10, fontWeight: 700 }} data-testid={`pj-listing-${it.id}`}>
                            ⏳ Belum Listing
                          </span>
                        )}
                        {it.listing_carry_status === "Menunggu" && (
                          <span className="vb-badge" style={{ display: "block", marginTop: 3, background: "#dbeafe", color: "#1e3a8a", border: "1px solid #93c5fd", fontSize: 9, fontWeight: 700 }}
                            title="Website sudah dibayar, biaya Listing sengaja ditunda ke periode berikutnya">
                            ⏳ Next Periode ({it.listing_carry_periode || "-"})
                          </span>
                        )}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {it.payment_status === "Lunas" ? (
                          <span className="vb-badge vb-badge-approved" style={{ marginRight: 3 }} title="Lunas"><CheckCircle2 className="w-3 h-3 inline" /></span>
                        ) : it.payment_status === "Sebagian" ? (
                          <span className="vb-badge vb-badge-pending" style={{ marginRight: 3 }} title={`Sebagian: ${usd(it.amount_paid)}`}>◑</span>
                        ) : (
                          <span className="vb-badge vb-badge-rejected" style={{ marginRight: 3 }} title="Pending">●</span>
                        )}
                        {it.payment_status !== "Lunas" && (
                          <button className="vb-btn-primary" style={{ padding: "2px 6px", marginRight: 3 }} onClick={() => {
                            setPayTarget(it);
                            // Kalau project punya Petugas Listing & belum Sudah Listing, default mode
                            // "next_periode_listing" (bayar Website saja dulu, sama seperti prefill
                            // amount_due yang sudah ada) — kalau tidak relevan, mode null (dikirim
                            // apa adanya ke backend, tidak berpengaruh karena amount_due==total_payment).
                            const relevantListing = it.listing_id && it.listing_status !== "Sudah Listing";
                            const defaultMode = relevantListing ? "next_periode_listing" : null;
                            setPayForm({ payment_date: today, amount_paid: Math.max(0, (it.amount_due ?? it.total_payment) - it.amount_paid), metode: "Transfer", catatan: "", mode: defaultMode });
                          }} data-testid={`pj-pay-${it.id}`}>
                            <Wallet className="w-3 h-3 inline" />
                          </button>
                        )}
                        <button
                          className="vb-btn"
                          style={{ padding: "2px 6px", marginRight: 3, background: isListing ? "#fff7b3" : "#d6f3c1", opacity: (!isListing && !it.listing_id) ? 0.5 : 1, cursor: (!isListing && !it.listing_id) ? "not-allowed" : "pointer" }}
                          disabled={!isListing && !it.listing_id}
                          title={!isListing && !it.listing_id ? "Isi Petugas Listing dulu (Edit project) sebelum bisa ditandai Sudah Listing" : (isListing ? "Klik untuk tandai BELUM LISTING" : "Klik untuk tandai SUDAH LISTING")}
                          onClick={async () => {
                            const next = isListing ? "Belum Listing" : "Sudah Listing";
                            try { await api.patch(`/projects/${it.id}/listing-status`, { listing_status: next }); toast.success(`Status listing diubah: ${next}`); load(); }
                            catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                          }}
                          data-testid={`pj-toggle-listing-${it.id}`}
                        >
                          {isListing ? "↩ Belum Listing" : "✓ Sudah Listing"}
                        </button>
                        <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 3 }} onClick={() => openEdit(it)}><Pencil className="w-3 h-3" /></button>
                        {(it.amount_paid || 0) === 0 && (
                          <button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => setDel(it)}><Trash2 className="w-3 h-3" /></button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "settings" && (
        <SettingsView exts={exts} types={types} devs={devs} designers={designers} listings={listings} listingRates={listingRates} groups={groups} clients={clients} users={users} tambahanList={tambahanList} reks={reks} reload={load} />
      )}

      {tab === "recap" && (
        <div>
          <h2 className="vb-h1" style={{ marginBottom: 8 }}>Rekap Per Periode (Due Date 15 / 30)</h2>
          {recap.length === 0 ? <EmptyState message="Belum ada data project" /> : (
            <table className="vb-table">
              <thead>
                <tr>
                  <th>Periode (Due Date)</th>
                  <th style={{ textAlign: "center" }}>Jumlah Project</th>
                  <th style={{ textAlign: "right" }}>Total Revenue</th>
                  <th style={{ textAlign: "right" }}>Terbayar</th>
                  <th style={{ textAlign: "right" }}>Outstanding</th>
                  <th>Status Periode</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {recap.map((r) => (
                  <tr key={r.periode}>
                    <td style={{ fontWeight: 700, color: "#003399" }}>{r.periode}</td>
                    <td style={{ textAlign: "center" }}>{r.count} ({r.count_lunas} lunas)</td>
                    <td style={{ textAlign: "right" }}><UsdIdr n={r.total} rate={usdIdrRate} /></td>
                    <td style={{ textAlign: "right" }}><UsdIdr n={r.paid} rate={usdIdrRate} color="#4f8a10" /></td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}><UsdIdr n={r.outstanding} rate={usdIdrRate} color="#d8000c" fontWeight={600} /></td>
                    <td>
                      {r.status_periode === "Terbayar" ? (
                        <span className="vb-badge vb-badge-approved">✓ Terbayar</span>
                      ) : (
                        <span className="vb-badge vb-badge-pending">Pending</span>
                      )}
                    </td>
                    <td>
                      <button className="vb-btn" style={{ padding: "2px 6px" }} onClick={() => loadRecapGroup(r.periode)} data-testid={`recap-group-${r.periode}`}>
                        Cek per Group
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h2 className="vb-h1" style={{ marginTop: 20, marginBottom: 8 }}>
            Rekap Per Group {recapGroupPeriode && <span style={{ fontSize: 12, color: "#003399" }}>— Periode {recapGroupPeriode}</span>}
          </h2>
          <div style={{ marginBottom: 8 }}>
            <button className="vb-btn" onClick={() => loadRecapGroup("")} data-testid="recap-all-group">Tampilkan Semua Periode</button>
          </div>
          {recapGroups.length === 0 ? <EmptyState message="Tidak ada data group untuk periode ini" /> : (
            <table className="vb-table">
              <thead>
                <tr>
                  <th>Group</th>
                  <th>Client</th>
                  <th style={{ textAlign: "center" }}>Jumlah Project</th>
                  <th style={{ textAlign: "right" }}>Total</th>
                  <th style={{ textAlign: "right" }}>Terbayar</th>
                  <th style={{ textAlign: "right" }}>Outstanding</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recapGroups.map((g, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 700 }}>{g.group_nama}</td>
                    <td style={{ fontSize: 11 }}>{g.clients.join(", ")}</td>
                    <td style={{ textAlign: "center" }}>{g.count} ({g.count_lunas} lunas)</td>
                    <td style={{ textAlign: "right" }}><UsdIdr n={g.total} rate={usdIdrRate} /></td>
                    <td style={{ textAlign: "right" }}><UsdIdr n={g.paid} rate={usdIdrRate} color="#4f8a10" /></td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}><UsdIdr n={g.outstanding} rate={usdIdrRate} color="#d8000c" fontWeight={600} /></td>
                    <td>
                      {g.status_periode === "Terbayar" ? (
                        <span className="vb-badge vb-badge-approved">✓ Terbayar</span>
                      ) : (
                        <span className="vb-badge vb-badge-pending">Pending</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Project Form Dialog.
          Catatan bug nyata yang ditemukan & diperbaiki di sini: tombol "Simpan" ini hidup di
          `footer` milik Dialog, yang dirender sebagai <div> TERPISAH di luar <form> (lihat
          komponen Dialog di VB.jsx). onClick={submit} manggil fungsi submit langsung lewat JS —
          ini TIDAK PERNAH memicu validasi native HTML5 `required` sama sekali, karena validasi
          itu cuma jalan kalau tombol type="submit" benar-benar ada DI DALAM elemen <form> (atau
          form.requestSubmit() dipanggil). Akibatnya field required (Type Web, dst.) diam-diam
          bisa lolos kosong tanpa peringatan apa pun — inilah sebab nyata project Material Only
          bisa tersimpan dengan Type Web kosong (jadi Rate Listing tidak ketemu). Diperbaiki
          dengan atribut `form` HTML5 (bisa menghubungkan tombol ke <form> lain di DOM walau
          bukan anak langsungnya) — sekarang klik Simpan benar-benar men-submit <form> di bawah
          ini, validasi `required` browser jalan normal lagi. */}
      <Dialog open={open} onClose={() => setOpen(false)} title={editId ? "Edit Project" : "Tambah Project Baru"} width={680}
        footer={<><button className="vb-btn" onClick={() => setOpen(false)}>Batal</button><button type="submit" form="pj-project-form" className="vb-btn-primary" data-testid="pj-save">Simpan</button></>}>
        <form id="pj-project-form" onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div className="vb-form-row">
            <label className="vb-label">Nama Project*</label>
            <input className="vb-input" value={form.nama_project} onChange={(e) => setForm({ ...form, nama_project: e.target.value })} required data-testid="pj-nama" />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Type Order*</label>
            <select className="vb-select" value={form.type_order} onChange={(e) => handleTypeOrderChange(e.target.value)} required data-testid="pj-type-order">
              <option value="WEBSITE">Website</option>
              <option value="REBRAND">Rebrand</option>
              <option value="MATERIAL_ONLY">Material Only</option>
              <option value="LISTING_ONLY">Listing Only</option>
            </select>
            {isMaterialOnly && (
              <span style={{ fontSize: 10, color: "#666", marginTop: 2 }}>
                Tidak ada pembuatan website — domain otomatis "MATERIAL ONLY", Type Web tidak perlu dipilih, Developer/Designer dikosongkan (uang tidak masuk ke keduanya), harga flat {usd(MATERIAL_ONLY_PRICE_USD)}.
              </span>
            )}
            {isRebrand && (
              <span style={{ fontSize: 10, color: "#666", marginTop: 2 }}>
                Sama seperti order Website (semua field aktif/wajib), TAPI harga web hanya <strong>50% dari harga Type Web</strong>. Komponen lain (domain, Rate Listing, tambahan) tidak dipotong.
              </span>
            )}
            {isListingOnly && (
              <span style={{ fontSize: 10, color: "#666", marginTop: 2 }}>
                Bukan order website baru — untuk menagih Rate Listing periode ini atas website yang domain &amp; web-nya sudah lunas di periode sebelumnya (pilih "Project From" di bawah). Domain/Type Web otomatis terisi dari project itu &amp; tidak bisa diubah, Developer/Designer dikosongkan, Petugas Listing wajib diisi, status otomatis "Sudah Listing".
              </span>
            )}
          </div>
          {isListingOnly && (
            <div className="vb-form-row" style={{ gridColumn: "1 / -1" }}>
              <label className="vb-label">Project From*</label>
              <ProjectFromPicker value={form.source_project_id} initialLabel={form.source_project_id ? (form.source_project_nama || "") : ""} onPick={handleSourceProjectPick} />
              <span style={{ fontSize: 10, color: "#666", marginTop: 2 }}>
                Cari berdasarkan Nama Project atau Domain — penanda project mana yang listing-nya sedang ditagih periode ini (domain &amp; web project itu sendiri sudah lunas sebelumnya, tidak ditagih ulang di sini).
              </span>
            </div>
          )}
          <div className="vb-form-row">
            <label className="vb-label">Domain{isMaterialOnly ? "" : isListingOnly ? " (otomatis dari Project From)" : "* (auto-detect ext)"}</label>
            <input
              className="vb-input"
              value={form.domain}
              onChange={(e) => setForm({ ...form, domain: e.target.value.toLowerCase().trim() })}
              required={!isMaterialOnly && !isListingOnly}
              disabled={isMaterialOnly || isListingOnly}
              placeholder="example.com"
              data-testid="pj-domain"
              style={(isMaterialOnly || isListingOnly) ? { background: "#f0f0f0", color: "#888", fontWeight: 700 } : undefined}
            />
            {!isMaterialOnly && !isListingOnly && previewExt && (
              <span style={{ fontSize: 11, color: "#003399", marginTop: 2 }}>
                Ext: <strong>{previewExt}</strong> → Price: <strong>{form.free_domain ? <span style={{ color: "#4f8a10", textDecoration: "line-through" }}>{usd(exts.find((x) => x.ext === previewExt)?.price_usd || 0)}</span> : usd(previewPriceDomain)}</strong>
                {form.free_domain && <span style={{ color: "#4f8a10", marginLeft: 6, fontWeight: 700 }}>FREE</span>}
              </span>
            )}
            <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, marginTop: 4, padding: 4, background: form.free_domain ? "#e8f7e8" : "#f5f5f5", border: "1px solid " + (form.free_domain ? "#4f8a10" : "#ccc"), opacity: (isMaterialOnly || isListingOnly) ? 0.5 : 1 }}>
              <input type="checkbox" checked={!!form.free_domain} onChange={(e) => setForm({ ...form, free_domain: e.target.checked })} disabled={isMaterialOnly || isListingOnly} data-testid="pj-free-domain" />
              <span style={{ fontWeight: 600 }}>Free Domain</span>
              <span style={{ fontSize: 11, color: "#666" }}>(membebaskan biaya domain — $0)</span>
            </label>
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Link Twitter (opsional)</label>
            <input
              className="vb-input"
              value={form.link_twitter}
              onChange={(e) => setForm({ ...form, link_twitter: e.target.value })}
              placeholder="https://twitter.com/namaproject atau @namaproject"
              data-testid="pj-twitter"
            />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">{isMaterialOnly ? "Type Web (tidak berlaku untuk Material Only)" : isListingOnly ? "Type Web (otomatis dari Project From)" : "Type Web*"}</label>
            <select
              className="vb-select"
              value={isMaterialOnly ? MATERIAL_ONLY_TYPE_WEB : form.type_web}
              onChange={(e) => setForm({ ...form, type_web: e.target.value })}
              required={!isMaterialOnly && !isListingOnly}
              disabled={isMaterialOnly || isListingOnly}
              data-testid="pj-type"
              style={(isMaterialOnly || isListingOnly) ? { background: "#f0f0f0", color: "#888" } : undefined}
            >
              {isMaterialOnly ? (
                <option value={MATERIAL_ONLY_TYPE_WEB}>{MATERIAL_ONLY_TYPE_WEB} (bukan pembuatan website)</option>
              ) : (
                <>
                  <option value="">- pilih -</option>
                  {types.map((t) => <option key={t.id} value={t.nama}>{t.nama} ({usd(t.price_usd)})</option>)}
                </>
              )}
            </select>
            {isRebrand && form.type_web && rawPriceWeb > 0 && (
              <span style={{ fontSize: 11, color: "#003399", marginTop: 2 }}>
                Harga Type Web {usd(rawPriceWeb)} → ditagihkan <strong>{usd(rawPriceWeb * REBRAND_PRICE_MULTIPLIER)}</strong> (Rebrand 50%)
              </span>
            )}
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Take By (Client)*</label>
            <select className="vb-select" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} required data-testid="pj-client">
              <option value="">- pilih client -</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.nama} ({c.group_nama})</option>)}
            </select>
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Developer{(isMaterialOnly || isListingOnly) ? ` (nonaktif untuk ${isMaterialOnly ? "Material Only" : "Listing Only"})` : "*"}</label>
            <select className="vb-select" value={(isMaterialOnly || isListingOnly) ? "" : (form.developer_id || "")} onChange={(e) => setForm({ ...form, developer_id: e.target.value })} required={!isMaterialOnly && !isListingOnly} disabled={isMaterialOnly || isListingOnly} data-testid="pj-dev">
              <option value="">- pilih developer -</option>
              {devs.map((d) => <option key={d.id} value={d.id}>{d.nama}</option>)}
            </select>
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Designer{(isMaterialOnly || isListingOnly) ? ` (nonaktif untuk ${isMaterialOnly ? "Material Only" : "Listing Only"})` : " (opsional)"}</label>
            <select className="vb-select" value={(isMaterialOnly || isListingOnly) ? "" : (form.designer_id || "")} onChange={(e) => setForm({ ...form, designer_id: e.target.value })} disabled={isMaterialOnly || isListingOnly} data-testid="pj-designer">
              <option value="">- belum ada designer -</option>
              {designers.map((d) => <option key={d.id} value={d.id}>{d.nama}</option>)}
            </select>
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Petugas Listing{isListingOnly ? "*" : " (opsional)"}</label>
            <select className="vb-select" value={form.listing_id || ""} onChange={(e) => setForm({ ...form, listing_id: e.target.value })} required={isListingOnly} data-testid="pj-listing">
              <option value="">- belum ada petugas -</option>
              {listings.map((l) => <option key={l.id} value={l.id}>{l.nama}</option>)}
            </select>
            <span style={{ fontSize: 10, color: "#666", marginTop: 2 }}>
              {isListingOnly
                ? "Wajib diisi untuk Type Order Listing Only — inilah biaya yang benar-benar ditagih periode ini."
                : "Kosong = belum ada petugas → status otomatis \"Belum Listing\" & Rate Listing tidak ditambahkan ke total."}
            </span>
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Tanggal Pembuatan*</label>
            <input type="date" className="vb-input" value={form.tanggal_pembuatan} onChange={(e) => setForm({ ...form, tanggal_pembuatan: e.target.value })} required />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Tambahan (USD, opsional)</label>
            <input type="number" step="0.01" className="vb-input" value={form.tambahan} onChange={(e) => setForm({ ...form, tambahan: e.target.value })} data-testid="pj-tambahan" />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Keterangan Tambahan</label>
            <input
              className="vb-input"
              value={form.tambahan_keterangan}
              onChange={(e) => setForm({ ...form, tambahan_keterangan: e.target.value })}
              placeholder="Misal: SSL, Hosting Setup, Logo Design"
              disabled={!form.tambahan || parseFloat(form.tambahan) === 0}
              data-testid="pj-tambahan-ket"
            />
          </div>
          <div className="vb-form-row" style={{ gridColumn: "1 / 3" }}>
            <label className="vb-label">Tambahan Terdaftar (Pengaturan → Tambahan)</label>
            {tambahanList.filter((t) => t.status_aktif !== false).length === 0 ? (
              <div style={{ fontSize: 11, color: "#666", padding: 6, background: "#fafafa", border: "1px dashed #ccc" }}>
                Belum ada tambahan terdaftar. Buat di tab Pengaturan → Tambahan.
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 4, padding: 6, background: "#fafafa", border: "1px solid #ccc", maxHeight: 180, overflow: "auto" }}>
                {tambahanList.filter((t) => t.status_aktif !== false).map((t) => {
                  const checked = (form.tambahan_ids || []).includes(t.id);
                  const debet = !!t.rekening_id_debet;
                  return (
                    <label key={t.id} style={{ fontSize: 12, display: "flex", gap: 6, padding: 6, background: checked ? "#e8f4ff" : "white", border: "1px solid " + (checked ? "#003399" : "#e0e0e0"), cursor: "pointer" }}>
                      <input type="checkbox" checked={checked} onChange={(e) => {
                        const ids = new Set(form.tambahan_ids || []);
                        if (e.target.checked) ids.add(t.id); else ids.delete(t.id);
                        setForm({ ...form, tambahan_ids: Array.from(ids) });
                      }} data-testid={`pj-tambahan-${t.id}`} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>{t.nama}</div>
                        <div style={{ fontSize: 10, color: "#555" }}>
                          {t.currency === "USD" ? usd(t.nominal) : "Rp " + parseFloat(t.nominal || 0).toLocaleString("id-ID")}
                          {debet ? (
                            <span style={{ color: "#4f8a10" }}> · ditebet dari <strong>{t.rekening_nama}</strong></span>
                          ) : (
                            <span style={{ color: "#d8000c" }}> · masuk ke tagihan client</span>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            {tambahanFromRekening.length > 0 && (
              <div style={{ marginTop: 6, padding: 6, background: "#fff8dc", border: "1px solid #e0c97f", fontSize: 11 }}>
                💡 {tambahanFromRekening.length} tambahan akan diambil dari rekening (tidak masuk tagihan client):
                <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                  {tambahanFromRekening.map((t) => <li key={t.id}>{t.nama} → {t.rekening_nama} ({t.currency === "USD" ? usd(t.nominal) : "Rp " + parseFloat(t.nominal || 0).toLocaleString("id-ID")})</li>)}
                </ul>
              </div>
            )}
          </div>
          <div className="vb-form-row" style={{ gridColumn: "1 / 3" }}>
            <label className="vb-label">Catatan</label>
            <textarea rows={2} className="vb-textarea" value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} />
          </div>
          {/* Preview Total */}
          <div style={{ gridColumn: "1 / 3", background: "#003399", color: "white", padding: 10, borderRadius: 4, marginTop: 6 }}>
            <div style={{ fontSize: 12, opacity: 0.9 }}>Preview Total Payment (yang dibayar Client):</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              {isMaterialOnly ? (
                <>Material Only {usd(previewPriceWeb)}</>
              ) : isListingOnly ? (
                <>Listing Only — Domain &amp; Web sudah lunas periode sebelumnya {usd(0)}</>
              ) : (
                <>Domain {usd(previewPriceDomain)}{form.free_domain && <span style={{ color: "#a7f3a7", marginLeft: 4 }}>(Free)</span>} + Web {usd(previewPriceWeb)}{isRebrand && <span style={{ color: "#a7f3a7", marginLeft: 4 }}>(Rebrand 50%)</span>}</>
              )} + Rate Listing {usd(previewPriceListing)} + Tambahan-Manual {usd(form.tambahan || 0)} + Tambahan-Terdaftar(client) {usd(tambahanToTotalUSD)}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>
              <UsdIdr n={previewTotal} rate={usdIdrRate} align="left" fontSize={22} fontWeight={800} invert />
            </div>
          </div>
        </form>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={!!payTarget} onClose={() => setPayTarget(null)} title="Pembayaran Project" width={460}
        footer={<><button className="vb-btn" onClick={() => setPayTarget(null)}>Batal</button><button className="vb-btn-primary" onClick={submitPay} data-testid="pj-pay-save">Bayarkan</button></>}>
        {payTarget && (
          <>
            <div style={{ background: "#FFFFE1", border: "1px solid #828790", padding: 8, fontSize: 12, marginBottom: 12 }}>
              <strong>{payTarget.nama_project}</strong> ({payTarget.domain})<br />
              {(() => {
                const relevantListing = payTarget.listing_id && payTarget.listing_status !== "Sudah Listing";
                const due = relevantListing && payForm.mode === "full_payment"
                  ? payTarget.total_payment
                  : (payTarget.amount_due ?? payTarget.total_payment);
                const sisa = due - payTarget.amount_paid;
                return (
                  <>
                    Total: {usd(payTarget.total_payment)}
                    {(payTarget.pay_percent ?? 100) < 100 && (
                      <> &middot; <span style={{ color: "#7c3aed", fontWeight: 700 }}>Rating {payTarget.client_rating}★ → perlu dibayar {payTarget.pay_percent}%</span></>
                    )}
                    {parseFloat(payTarget.tip_amount || 0) > 0 && (
                      <> &middot; <span style={{ color: "#7c3aed", fontWeight: 700 }}>+ Tip {usd(payTarget.tip_amount)}</span></>
                    )}
                    {((payTarget.pay_percent ?? 100) < 100 || parseFloat(payTarget.tip_amount || 0) > 0) && (
                      <> &middot; <span style={{ color: "#166534", fontWeight: 700 }}>= {usd(due)}</span></>
                    )}
                    {" "}&middot; Terbayar: {usd(payTarget.amount_paid)} &middot; <strong style={{ color: "#d8000c" }}>Sisa: {usd(sisa)}</strong> <span style={{ fontSize: 10, color: "#888", fontStyle: "italic" }}>(≈ {idr(sisa * usdIdrRate)})</span>
                  </>
                );
              })()}
            </div>
            {payTarget.listing_id && payTarget.listing_status !== "Sudah Listing" && (
              <div className="vb-form-row">
                <label className="vb-label">Mode Pembayaran</label>
                <select className="vb-select" value={payForm.mode || "next_periode_listing"} data-testid="pj-pay-mode"
                  onChange={(e) => {
                    const mode = e.target.value;
                    const dueForMode = mode === "full_payment" ? payTarget.total_payment : (payTarget.amount_due ?? payTarget.total_payment);
                    setPayForm({ ...payForm, mode, amount_paid: Math.max(0, dueForMode - payTarget.amount_paid) });
                  }}>
                  <option value="next_periode_listing">Next Periode Listing (Website saja, Listing ditunda)</option>
                  <option value="full_payment">Full Payment (Website + Listing sekaligus)</option>
                </select>
                <div style={{ fontSize: 10, color: "#666", marginTop: 3 }}>
                  {payForm.mode === "full_payment"
                    ? "Client bayar Website + Listing sekaligus walau belum listing. Status listing tetap ditandai manual belakangan begitu benar-benar selesai."
                    : "Client bayar Website saja sekarang. Biaya Listing (belum ditagih) otomatis ditandai 'Menunggu Next Periode' supaya bisa ditagih ulang setelah listing selesai."}
                </div>
              </div>
            )}
            <div className="vb-form-row"><label className="vb-label">Tanggal Bayar*</label>
              <input type="date" className="vb-input" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} /></div>
            <div className="vb-form-row"><label className="vb-label">Jumlah (USD)*</label>
              <input type="number" step="0.01" className="vb-input" value={payForm.amount_paid} onChange={(e) => setPayForm({ ...payForm, amount_paid: e.target.value })} data-testid="pj-pay-amount" /></div>
            <div className="vb-form-row"><label className="vb-label">Metode</label>
              <select className="vb-select" value={payForm.metode} onChange={(e) => setPayForm({ ...payForm, metode: e.target.value })}>
                <option>Transfer</option><option>Cash</option><option>Other</option>
              </select></div>
            <div className="vb-form-row"><label className="vb-label">Catatan</label>
              <textarea rows={2} className="vb-textarea" value={payForm.catatan} onChange={(e) => setPayForm({ ...payForm, catatan: e.target.value })} /></div>
          </>
        )}
      </Dialog>

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} message={`Hapus project ${del?.nama_project}?`} />

      {/* Review permintaan revisi Group Client — cell abu-abu "Pending Rev". Setujui
          mengembalikan cell ke warna listing_status asli (hijau/kuning). */}
      <Dialog open={!!revisionView} onClose={() => setRevisionView(null)} title="Permintaan Revisi" width={480}
        footer={<><button className="vb-btn" onClick={() => setRevisionView(null)}>Tutup</button><button className="vb-btn-primary" onClick={approveRevision} data-testid="pj-revisi-approve">✓ Setujui</button></>}>
        {revisionView && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#003399", marginBottom: 6 }}>{revisionView.nama_project}</div>
            <div style={{ fontSize: 11, color: "#666", marginBottom: 10 }}>
              Diajukan oleh <strong>{revisionView.revision_requested_by_nama || "Group Client"}</strong>
              {revisionView.revision_requested_at && ` · ${new Date(revisionView.revision_requested_at).toLocaleString("id-ID")}`}
            </div>
            <div style={{ background: "#f3f4f6", border: "1px solid #9ca3af", padding: 10, fontSize: 13, whiteSpace: "pre-wrap" }}>
              {revisionView.revision_note}
            </div>
            <div style={{ fontSize: 11, color: "#666", marginTop: 8 }}>
              Klik <strong>Setujui</strong> setelah revisi selesai dikerjakan — status project akan kembali normal (Sudah/Belum Listing).
            </div>
          </>
        )}
      </Dialog>
    </Window>
  );
}

// Settings sub-component
function SettingsView({ exts, types, devs, designers, listings, listingRates, groups, clients, users, tambahanList, reks, reload }) {
  // Akun Pengguna ber-role "client" yang bisa ditautkan ke Client / Group Client. Admin membuat
  // akunnya lebih dulu di menu Pengguna (role "Client"), lalu tautkan di sini.
  const clientUserOptions = (users || [])
    .filter((u) => u.role === "client")
    .map((u) => ({ id: u.id, nama: `${u.name} — ${u.email}` }));
  const [section, setSection] = useState("ext");
  const sections = [
    { k: "ext", l: "Ext. Domain", icon: Globe },
    { k: "type", l: "Type Web", icon: DollarSign },
    { k: "dev", l: "Developer", icon: SettingsIcon },
    { k: "designer", l: "Designer", icon: SettingsIcon },
    { k: "listing", l: "Listing", icon: SettingsIcon },
    { k: "listing_rate", l: "Rate Harga Listing", icon: DollarSign },
    { k: "group", l: "Group Client", icon: SettingsIcon },
    { k: "client", l: "Client", icon: SettingsIcon },
    { k: "tambahan", l: "Tambahan (Bluetick-like)", icon: SettingsIcon },
    { k: "payinfo", l: "Info Pembayaran Perusahaan", icon: Wallet },
  ];

  return (
    <div>
      <div style={{ display: "flex", gap: 4, marginBottom: 10, borderBottom: "1px solid #828790", flexWrap: "wrap" }}>
        {sections.map((s) => (
          <button key={s.k} onClick={() => setSection(s.k)} className={`vb-btn ${section === s.k ? "vb-btn-primary" : ""}`} style={{ borderRadius: "3px 3px 0 0" }} data-testid={`set-tab-${s.k}`}>
            {s.l}
          </button>
        ))}
      </div>
      {section === "ext" && <SimpleSetting endpoint="/domain-ext" items={exts} reload={reload} fields={[{ k: "ext", l: "Extension (.com, .id)" }, { k: "price_usd", l: "Price (USD)", t: "number" }]} title="Ekstensi Domain" />}
      {section === "type" && <SimpleSetting endpoint="/web-types" items={types} reload={reload} fields={[{ k: "nama", l: "Nama Type (Standart/Proper)" }, { k: "price_usd", l: "Price (USD)", t: "number" }, { k: "deskripsi", l: "Deskripsi" }]} title="Type Web" />}
      {section === "dev" && <SimpleSetting endpoint="/developers" items={devs} reload={reload} fields={[{ k: "nama", l: "Nama Developer" }, { k: "kontak", l: "Kontak" }, { k: "email", l: "Email" }]} title="Developer" />}
      {section === "designer" && <SimpleSetting endpoint="/designers" items={designers} reload={reload} fields={[{ k: "nama", l: "Nama Designer" }, { k: "kontak", l: "Kontak" }, { k: "email", l: "Email" }]} title="Designer" />}
      {section === "listing" && <SimpleSetting endpoint="/listings" items={listings} reload={reload} fields={[{ k: "nama", l: "Nama Petugas Listing" }, { k: "kontak", l: "Kontak" }, { k: "email", l: "Email" }]} title="Listing" />}
      {section === "listing_rate" && (
        <div>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 8, padding: 6, background: "#fffbe6", border: "1px solid #d4a017" }}>
            💡 Rate ini ditambahkan LANGSUNG ke harga total project (Total Payment yang ditagihkan ke client), persis seperti Price Domain / Price Web — bukan gaji internal petugas listing.
            Pilih opsi "{MATERIAL_ONLY_TYPE_WEB}" untuk mengatur Rate Listing khusus order tipe Material Only (order ini tidak punya Type Web asli).
          </div>
          <SimpleSetting endpoint="/listing-rates" items={listingRates} reload={reload}
            fields={[{ k: "type_web", l: "Type Web", t: "select", options: [...types.map((t) => ({ id: t.nama, nama: t.nama })), { id: MATERIAL_ONLY_TYPE_WEB, nama: MATERIAL_ONLY_TYPE_WEB }] }, { k: "rate_usd", l: "Rate Tambahan (USD)", t: "number" }]}
            title="Rate Harga Listing (per Type Web)" />
        </div>
      )}
      {section === "group" && (
        <div>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 8, padding: 6, background: "#fffbe6", border: "1px solid #d4a017" }}>
            💡 Tautkan Group ke akun <strong>Pengguna</strong> (buat dulu di menu Pengguna dengan role <strong>Client</strong>). Akun yang ditaut akan otomatis melihat portal <code>/client</code> berisi laporan <strong>seluruh project</strong> milik client-client dalam group ini.
          </div>
          <SimpleSetting endpoint="/project-groups" items={groups} reload={reload}
            fields={[
              { k: "nama", l: "Nama Group" },
              { k: "deskripsi", l: "Deskripsi" },
              { k: "user_id", l: "Tautkan ke Pengguna (Group Client)", t: "select", options: clientUserOptions,
                render: (it) => it.user_nama ? `${it.user_nama} (${it.user_email})` : "-" },
            ]}
            title="Group Client" />
        </div>
      )}
      {section === "client" && <ClientSettingWithAccount items={clients} groups={groups} clientUserOptions={clientUserOptions} reload={reload} />}
      {section === "tambahan" && <TambahanSetting items={tambahanList} reks={reks} reload={reload} />}
      {section === "payinfo" && <CompanyPaymentInfoSetting />}
    </div>
  );
}

// Company Payment Info CRUD (Bank + Crypto destinations)
function CompanyPaymentInfoSetting() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const empty = { type: "bank", label: "", currency: "IDR", bank_nama: "", no_rekening: "", atas_nama: "", swift_code: "", network: "", wallet_address: "", memo_tag: "", instruksi: "", status_aktif: true, urutan: 0 };
  const [form, setForm] = useState(empty);
  const [del, setDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/company-payment-info"); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editId) await api.put(`/company-payment-info/${editId}`, form);
      else await api.post("/company-payment-info", form);
      toast.success("Tersimpan"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/company-payment-info/${del.id}`); toast.success("Dihapus"); setDel(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <h3 className="vb-h1" style={{ margin: 0 }}>Info Pembayaran Perusahaan (Bank &amp; Crypto)</h3>
        <button className="vb-btn-primary" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-payinfo">
          <Plus className="w-3 h-3 inline mr-1" />Tambah
        </button>
      </div>
      <div style={{ fontSize: 11, color: "#666", marginBottom: 8, padding: 6, background: "#fffbe6", border: "1px solid #d4a017" }}>
        💡 Info ini akan tampil di dashboard client agar mereka tahu kemana harus transfer. Buat satu entry per rekening bank atau wallet crypto. Tandai aktif/nonaktif sesuai kebutuhan.
      </div>
      {loading ? <Loading /> : items.length === 0 ? <EmptyState message="Belum ada info pembayaran" /> : (
        <table className="vb-table">
          <thead><tr><th>Tipe</th><th>Label</th><th>Detail</th><th>Status</th><th>Aksi</th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id}>
                <td>{p.type === "bank" ? <span className="vb-badge vb-badge-izin">BANK</span> : <span className="vb-badge vb-badge-approved">CRYPTO</span>}</td>
                <td><strong>{p.label}</strong><div style={{ fontSize: 10, color: "#666" }}>{p.currency}</div></td>
                <td style={{ fontSize: 11 }}>
                  {p.type === "bank" ? (
                    <>{p.bank_nama} <code>{p.no_rekening}</code> a/n <strong>{p.atas_nama}</strong>{p.swift_code && ` · SWIFT ${p.swift_code}`}</>
                  ) : (
                    <>{p.network} · <code style={{ wordBreak: "break-all" }}>{p.wallet_address}</code>{p.memo_tag && ` · MEMO ${p.memo_tag}`}</>
                  )}
                  {p.instruksi && <div style={{ fontSize: 10, color: "#999", marginTop: 2, fontStyle: "italic" }}>{p.instruksi}</div>}
                </td>
                <td>{p.status_aktif ? <span className="vb-badge vb-badge-hadir">Aktif</span> : <span className="vb-badge vb-badge-tidakhadir">Nonaktif</span>}</td>
                <td>
                  <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 4 }} onClick={() => { setForm(p); setEditId(p.id); setOpen(true); }}><Pencil className="w-3 h-3" /></button>
                  <button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => setDel(p)}><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} title={editId ? "Edit Info Pembayaran" : "Tambah Info Pembayaran"} width={520}
        footer={<><button className="vb-btn" onClick={() => setOpen(false)}>Batal</button><button className="vb-btn-primary" onClick={save} data-testid="payinfo-save">Simpan</button></>}>
        <div className="vb-form-row"><label className="vb-label">Tipe*</label>
          <select className="vb-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="payinfo-type">
            <option value="bank">Bank Transfer</option>
            <option value="crypto">Crypto Wallet</option>
          </select></div>
        <div className="vb-form-row"><label className="vb-label">Label*</label>
          <input className="vb-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Mis: BCA Operasional / USDT TRC20" required data-testid="payinfo-label" /></div>
        <div className="vb-form-row"><label className="vb-label">Mata Uang*</label>
          <input className="vb-input" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} placeholder="IDR / USD / USDT / BTC / ETH" required data-testid="payinfo-currency" /></div>

        {form.type === "bank" ? (
          <>
            <div className="vb-form-row"><label className="vb-label">Nama Bank*</label>
              <input className="vb-input" value={form.bank_nama || ""} onChange={(e) => setForm({ ...form, bank_nama: e.target.value })} placeholder="BCA / Mandiri / BRI" data-testid="payinfo-bank-nama" /></div>
            <div className="vb-form-row"><label className="vb-label">No. Rekening*</label>
              <input className="vb-input" value={form.no_rekening || ""} onChange={(e) => setForm({ ...form, no_rekening: e.target.value })} data-testid="payinfo-no-rek" /></div>
            <div className="vb-form-row"><label className="vb-label">Atas Nama*</label>
              <input className="vb-input" value={form.atas_nama || ""} onChange={(e) => setForm({ ...form, atas_nama: e.target.value })} data-testid="payinfo-atas-nama" /></div>
            <div className="vb-form-row"><label className="vb-label">SWIFT Code (opsional untuk transfer internasional)</label>
              <input className="vb-input" value={form.swift_code || ""} onChange={(e) => setForm({ ...form, swift_code: e.target.value })} /></div>
          </>
        ) : (
          <>
            <div className="vb-form-row"><label className="vb-label">Network*</label>
              <input className="vb-input" value={form.network || ""} onChange={(e) => setForm({ ...form, network: e.target.value })} placeholder="TRC20 / ERC20 / BEP20 / Polygon" data-testid="payinfo-network" /></div>
            <div className="vb-form-row"><label className="vb-label">Wallet Address*</label>
              <input className="vb-input" style={{ fontFamily: "Consolas, monospace" }} value={form.wallet_address || ""} onChange={(e) => setForm({ ...form, wallet_address: e.target.value })} data-testid="payinfo-wallet" /></div>
            <div className="vb-form-row"><label className="vb-label">Memo / Tag (opsional, wajib untuk XRP/EOS/Stellar)</label>
              <input className="vb-input" value={form.memo_tag || ""} onChange={(e) => setForm({ ...form, memo_tag: e.target.value })} /></div>
          </>
        )}

        <div className="vb-form-row"><label className="vb-label">Instruksi / Catatan (tampil di dashboard client)</label>
          <textarea rows={2} className="vb-textarea" value={form.instruksi || ""} onChange={(e) => setForm({ ...form, instruksi: e.target.value })} placeholder="Mis: Konfirmasi via WA setelah transfer. Hanya kirim USDT lewat jaringan TRC20." /></div>
        <div className="vb-form-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div>
            <label className="vb-label">Urutan tampil</label>
            <input type="number" className="vb-input" value={form.urutan || 0} onChange={(e) => setForm({ ...form, urutan: parseInt(e.target.value || 0) })} />
          </div>
          <div style={{ alignSelf: "end" }}>
            <label style={{ fontSize: 12 }}><input type="checkbox" checked={!!form.status_aktif} onChange={(e) => setForm({ ...form, status_aktif: e.target.checked })} /> Aktif (tampil di client)</label>
          </div>
        </div>
      </Dialog>
      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} message={`Hapus info pembayaran "${del?.label}"?`} />
    </div>
  );
}

// Client management — identitas & login diambil dari akun Pengguna yang ditaut (tidak ada lagi
// email/kontak/alamat standalone). Buat akunnya lebih dulu di menu Pengguna (role "Client").
function ClientSettingWithAccount({ items, groups, clientUserOptions, reload }) {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [del, setDel] = useState(null);

  const submit = async () => {
    try {
      const payload = { nama: form.nama, group_id: form.group_id || "", user_id: form.user_id || "" };
      if (editId) await api.put(`/clients/${editId}`, payload);
      else await api.post("/clients", payload);
      toast.success("Tersimpan"); setOpen(false); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/clients/${del.id}`); toast.success("Dihapus"); setDel(null); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <h3 className="vb-h1" style={{ margin: 0 }}>List Client</h3>
        <button className="vb-btn-primary" onClick={() => { setForm({}); setEditId(null); setOpen(true); }} data-testid="add-client">
          <Plus className="w-3 h-3 inline mr-1" />Tambah Client
        </button>
      </div>
      <div style={{ fontSize: 11, color: "#666", marginBottom: 8, padding: 6, background: "#fffbe6", border: "1px solid #d4a017" }}>
        💡 Client <strong>tidak dibuat sendiri</strong> — tautkan ke akun <strong>Pengguna</strong> (buat dulu di menu Pengguna dengan role <strong>Client</strong>). Identitas & login diambil dari akun itu. Setelah ditaut, Pengguna tersebut login ke <code>/client</code> untuk melihat daftar website &amp; rekap pembayaran per periode.
      </div>
      {items.length === 0 ? <EmptyState /> : (
        <table className="vb-table">
          <thead><tr><th>Nama Client</th><th>Group</th><th>Akun Pengguna Tertaut</th><th>Aksi</th></tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id}>
                <td><strong>{c.nama}</strong></td>
                <td>{c.group_nama || "-"}</td>
                <td style={{ fontSize: 11 }}>{c.user_nama ? <span>{c.user_nama} <span style={{ color: "#666" }}>({c.user_email})</span></span> : <span style={{ color: "#b45309" }}>— belum ditaut —</span>}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 4 }} onClick={() => { setForm(c); setEditId(c.id); setOpen(true); }}><Pencil className="w-3 h-3" /></button>
                  <button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => setDel(c)}><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} title={editId ? "Edit Client" : "Tambah Client"} width={500}
        footer={<><button className="vb-btn" onClick={() => setOpen(false)}>Batal</button><button className="vb-btn-primary" onClick={submit} data-testid="client-save">Simpan</button></>}>
        <div className="vb-form-row"><label className="vb-label">Nama Client*</label>
          <input className="vb-input" value={form.nama || ""} onChange={(e) => setForm({ ...form, nama: e.target.value })} required data-testid="client-nama" /></div>
        <div className="vb-form-row"><label className="vb-label">Group</label>
          <select className="vb-select" value={form.group_id || ""} onChange={(e) => setForm({ ...form, group_id: e.target.value })}>
            <option value="">- pilih -</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.nama}</option>)}
          </select></div>
        <div className="vb-form-row"><label className="vb-label">Tautkan ke Pengguna</label>
          <select className="vb-select" value={form.user_id || ""} onChange={(e) => setForm({ ...form, user_id: e.target.value })} data-testid="client-user">
            <option value="">- tidak ditaut -</option>
            {clientUserOptions.map((u) => <option key={u.id} value={u.id}>{u.nama}</option>)}
          </select>
          <span style={{ fontSize: 11, color: "#666" }}>Hanya akun Pengguna ber-role <strong>Client</strong>. Buat dulu di menu Pengguna kalau belum ada. Menaut akun ke Client ini otomatis melepasnya dari Client/Group lain.</span>
        </div>
      </Dialog>

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} message={`Hapus client "${del?.nama}"? Tautan ke akun Pengguna-nya (kalau ada) akan dilepas.`} />
    </div>
  );
}

// Tambahan-specific CRUD (Bluetick-style)
function TambahanSetting({ items, reks, reload }) {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const empty = { nama: "", nominal: 0, currency: "USD", rekening_id_debet: "", status_aktif: true, deskripsi: "" };
  const [form, setForm] = useState(empty);
  const [del, setDel] = useState(null);

  const submit = async () => {
    try {
      const payload = { ...form, nominal: parseFloat(form.nominal || 0), rekening_id_debet: form.rekening_id_debet || "" };
      if (editId) await api.put(`/project-tambahan/${editId}`, payload);
      else await api.post("/project-tambahan", payload);
      toast.success("Tersimpan"); setOpen(false); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/project-tambahan/${del.id}`); toast.success("Dihapus"); setDel(null); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <h3 className="vb-h1" style={{ margin: 0 }}>Tambahan Project (Bluetick-like)</h3>
        <button className="vb-btn-primary" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-tambahan">
          <Plus className="w-3 h-3 inline mr-1" />Tambah
        </button>
      </div>
      <div style={{ fontSize: 11, color: "#666", marginBottom: 8, padding: 6, background: "#fffbe6", border: "1px solid #d4a017" }}>
        💡 Cara kerja: Saat membuat project, checklist Tambahan yang berlaku. <strong>Jika rekening dipilih</strong>, dana didebet otomatis dari rekening tersebut (boleh saldo minus) dan TIDAK masuk ke tagihan client. <strong>Jika tanpa rekening</strong>, nominal di-include ke total tagihan client.
      </div>
      {items.length === 0 ? <EmptyState message="Belum ada tambahan terdaftar" /> : (
        <table className="vb-table">
          <thead><tr><th>Nama</th><th style={{ textAlign: "right" }}>Nominal</th><th>Mata Uang</th><th>Rekening Debet</th><th>Status</th><th>Aksi</th></tr></thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id}>
                <td><strong>{t.nama}</strong><div style={{ fontSize: 10, color: "#666" }}>{t.deskripsi}</div></td>
                <td style={{ textAlign: "right" }}>{t.currency === "USD" ? "$ " + parseFloat(t.nominal || 0).toFixed(2) : "Rp " + parseFloat(t.nominal || 0).toLocaleString("id-ID")}</td>
                <td>{t.currency}</td>
                <td>{t.rekening_id_debet ? <span className="vb-badge vb-badge-approved">{t.rekening_nama} ({t.rekening_currency})</span> : <span className="vb-badge vb-badge-pending">Include ke Tagihan Client</span>}</td>
                <td>{t.status_aktif ? <span className="vb-badge vb-badge-hadir">Aktif</span> : <span className="vb-badge vb-badge-tidakhadir">Nonaktif</span>}</td>
                <td>
                  <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 4 }} onClick={() => { setForm({ ...empty, ...t }); setEditId(t.id); setOpen(true); }} data-testid={`edit-tambahan-${t.id}`}><Pencil className="w-3 h-3" /></button>
                  <button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => setDel(t)} data-testid={`del-tambahan-${t.id}`}><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} title={editId ? "Edit Tambahan" : "Tambah Tambahan Baru"} width={500}
        footer={<><button className="vb-btn" onClick={() => setOpen(false)}>Batal</button><button className="vb-btn-primary" onClick={submit} data-testid="tambahan-save">Simpan</button></>}>
        <div className="vb-form-row"><label className="vb-label">Nama Tambahan*</label>
          <input className="vb-input" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="Mis: Bluetick, SSL Premium, Hosting Setup" required data-testid="tambahan-nama" /></div>
        <div className="vb-form-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div>
            <label className="vb-label">Nominal*</label>
            <input type="number" step="0.01" className="vb-input" value={form.nominal} onChange={(e) => setForm({ ...form, nominal: e.target.value })} required data-testid="tambahan-nominal" />
          </div>
          <div>
            <label className="vb-label">Mata Uang*</label>
            <select className="vb-select" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="tambahan-currency">
              <option value="USD">USD</option>
              <option value="IDR">IDR</option>
            </select>
          </div>
        </div>
        <div className="vb-form-row">
          <label className="vb-label">Rekening Debet (opsional)</label>
          <select className="vb-select" value={form.rekening_id_debet || ""} onChange={(e) => setForm({ ...form, rekening_id_debet: e.target.value })} data-testid="tambahan-rekening">
            <option value="">- TANPA REKENING (include ke tagihan client) -</option>
            {reks.map((r) => <option key={r.id} value={r.id}>{r.nama} ({r.currency}) — saldo {r.currency === "USD" ? "$ " + parseFloat(r.saldo || 0).toFixed(2) : "Rp " + parseFloat(r.saldo || 0).toLocaleString("id-ID")}</option>)}
          </select>
          <span style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
            Jika dipilih: dana otomatis ditebet dari rekening saat project dibuat (boleh minus). Jika tidak dipilih: nominal di-include ke total tagihan client.
          </span>
        </div>
        <div className="vb-form-row">
          <label className="vb-label">Deskripsi</label>
          <textarea rows={2} className="vb-textarea" value={form.deskripsi} onChange={(e) => setForm({ ...form, deskripsi: e.target.value })} />
        </div>
        <div className="vb-form-row">
          <label style={{ fontSize: 12 }}><input type="checkbox" checked={!!form.status_aktif} onChange={(e) => setForm({ ...form, status_aktif: e.target.checked })} /> Aktif (tampil di modal Project)</label>
        </div>
      </Dialog>
      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} message={`Hapus tambahan "${del?.nama}"?`} />
    </div>
  );
}

function SimpleSetting({ endpoint, items, reload, fields, title }) {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [del, setDel] = useState(null);

  const submit = async () => {
    try {
      const payload = { ...form };
      fields.forEach((f) => { if (f.t === "number") payload[f.k] = parseFloat(payload[f.k] || 0); });
      if (editId) await api.put(`${endpoint}/${editId}`, payload);
      else await api.post(endpoint, payload);
      toast.success("Tersimpan"); setOpen(false); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`${endpoint}/${del.id}`); toast.success("Dihapus"); setDel(null); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <h3 className="vb-h1" style={{ margin: 0 }}>{title}</h3>
        <button className="vb-btn-primary" onClick={() => { setForm({}); setEditId(null); setOpen(true); }} data-testid={`add-set-${endpoint}`}>
          <Plus className="w-3 h-3 inline mr-1" />Tambah
        </button>
      </div>
      {items.length === 0 ? <EmptyState /> : (
        <table className="vb-table">
          <thead><tr>{fields.map((f) => <th key={f.k}>{f.l}</th>)}<th>Aksi</th></tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id}>
                {fields.map((f) => {
                  let v = f.render ? f.render(it) : it[f.k];
                  if (!f.render && (f.k === "price_usd" || f.k === "rate_usd")) v = "$ " + parseFloat(v || 0).toFixed(2);
                  return <td key={f.k}>{v || "-"}</td>;
                })}
                <td>
                  <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 4 }} onClick={() => { setForm(it); setEditId(it.id); setOpen(true); }}><Pencil className="w-3 h-3" /></button>
                  <button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => setDel(it)}><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} title={editId ? `Edit ${title}` : `Tambah ${title}`}
        footer={<><button className="vb-btn" onClick={() => setOpen(false)}>Batal</button><button className="vb-btn-primary" onClick={submit} data-testid="set-save">Simpan</button></>}>
        {fields.map((f) => (
          <div key={f.k} className="vb-form-row">
            <label className="vb-label">{f.l}</label>
            {f.t === "select" ? (
              <select className="vb-select" value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}>
                <option value="">- pilih -</option>
                {(f.options || []).map((o) => <option key={o.id} value={o.id}>{o.nama}</option>)}
              </select>
            ) : (
              <input type={f.t || "text"} step={f.t === "number" ? "0.01" : undefined} className="vb-input" value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} />
            )}
          </div>
        ))}
      </Dialog>

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} message="Yakin hapus?" />
    </div>
  );
}


// Export Excel button (with optional wallet address input)
function ExportExcelButton({ filterBulan, filterPeriode }) {
  const [open, setOpen] = useState(false);
  const [wallet, setWallet] = useState("");
  const [busy, setBusy] = useState(false);

  const doExport = async () => {
    setBusy(true);
    try {
      const resp = await api.get("/projects/export-xlsx", {
        params: { bulan: filterBulan || "", wallet_address: wallet || "" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `payment_website_${filterBulan || "all"}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export berhasil diunduh");
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal export");
    }
    setBusy(false);
  };

  return (
    <>
      <button className="vb-btn" onClick={() => setOpen(true)} data-testid="pj-export-xlsx" style={{ background: "linear-gradient(to bottom, #d6f0d6, #a7d9a7)", border: "1px solid #2E5E2E", color: "#0a4d0a", fontWeight: 600 }}>
        <FileSpreadsheet className="w-3 h-3 inline mr-1" />Export Excel
      </button>
      <Dialog open={open} onClose={() => setOpen(false)} title="Export Payment Website ke Excel" width={500}
        footer={<>
          <button className="vb-btn" onClick={() => setOpen(false)}>Batal</button>
          <button className="vb-btn-primary" onClick={doExport} disabled={busy} data-testid="pj-export-confirm">
            {busy ? "Mengunduh..." : "Download .xlsx"}
          </button>
        </>}>
        <div style={{ background: "#FFFFE1", border: "1px solid #828790", padding: 8, marginBottom: 10, fontSize: 12 }}>
          File akan diformat sesuai template (header hijau gelap, badge warna untuk Type & Developer, total payment, dan footer Wallet/TXID).
          {filterBulan && <div style={{ marginTop: 4 }}>Filter periode: <strong>{filterBulan}</strong></div>}
          {filterPeriode && filterPeriode !== "all" && <div style={{ marginTop: 4 }}>Filter payment: <strong>{filterPeriode === "15" ? "Periode 1-15" : "Periode 16-30/31"}</strong></div>}
          {!filterBulan && <div style={{ marginTop: 4, color: "#d8000c" }}>Semua periode akan diekspor.</div>}
        </div>
        <div className="vb-form-row">
          <label className="vb-label">Wallet Address (opsional, tampil di footer)</label>
          <input className="vb-input" value={wallet} onChange={(e) => setWallet(e.target.value)} placeholder="mis: 4wcU2uit29ULtYL4FtQ6p8bXc758QKUheqMGm89Bkfev" data-testid="pj-export-wallet" />
        </div>
      </Dialog>
    </>
  );
}
